import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { getReportPipelineStatusCacheKey } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine';
import { ReportStatus, type ReportPipelineStatus } from '@/types/report';

const POLL_INTERVAL_MS = 15_000;

function isTerminalPipelineReportStatus(status: ReportStatus | undefined) {
    return status === ReportStatus.COMPLETED || status === ReportStatus.ERROR;
}

interface UseReportPipelineStatusResult {
    pipelineStatus: ReportPipelineStatus | undefined;
    error: Error | undefined;
}

export function useReportPipelineStatus(
    reportId: string | null,
): UseReportPipelineStatusResult {
    const [pollingIntervalMs, setPollingIntervalMs] =
        useState(POLL_INTERVAL_MS);
    const lastKnownStatusRef = useRef<ReportPipelineStatus | undefined>(
        undefined,
    );

    const { data, error } = useSWR<ReportPipelineStatus>(
        reportId ? getReportPipelineStatusCacheKey(reportId) : null,
        reportId ? () => EngineService.getReportPipelineStatus(reportId) : null,
        {
            refreshInterval: pollingIntervalMs,
            revalidateOnFocus: false,
        },
    );

    useEffect(() => {
        lastKnownStatusRef.current = undefined;
    }, [reportId]);

    useEffect(() => {
        if (data !== undefined) {
            lastKnownStatusRef.current = data;
        }
    }, [data]);

    useEffect(() => {
        const status = data?.status ?? lastKnownStatusRef.current?.status;
        setPollingIntervalMs(
            isTerminalPipelineReportStatus(status) ? 0 : POLL_INTERVAL_MS,
        );
    }, [data, reportId]);

    return {
        pipelineStatus: data ?? lastKnownStatusRef.current,
        error: error as Error | undefined,
    };
}
